// Listings sort in the page rather than in the query: both quest listings
// already load in full for the counts and the rung grouping, so a sorted round
// trip would buy nothing.

export type SortValue = string | number | null;

export type Sort = {
  key: string;
  isDescending: boolean;
};

// The minimum a column has to expose to be sortable. Kept structural so this
// helper does not depend on the table component.
export type SortableColumn<TRow> = {
  key: string;
  sortValue?: (row: TRow) => SortValue;
};

// `numeric` so "rung 2" sorts before "rung 10", `base` so case and accents do
// not split otherwise equal labels.
const collator = new Intl.Collator("en-GB", {
  sensitivity: "base",
  numeric: true,
});

const compare = (left: SortValue, right: SortValue): number => {
  // Nulls sink in both directions: a row with nothing in the column floating
  // to the top on a descending sort is noise, not an answer.
  if (left === null || right === null) {
    return left === right ? 0 : left === null ? 1 : -1;
  }

  return typeof left === "number" && typeof right === "number"
    ? left - right
    : collator.compare(String(left), String(right));
};

// Array#sort is stable, so rows the column cannot tell apart keep the order the
// query gave them.
export const sortRows = <TRow>(
  rows: TRow[],
  columns: readonly SortableColumn<TRow>[],
  sort: Sort | undefined,
): TRow[] => {
  const sortValue = columns.find(
    (column) => column.key === sort?.key,
  )?.sortValue;

  if (sort === undefined || sortValue === undefined) {
    return rows;
  }

  const direction = sort.isDescending ? -1 : 1;

  return [...rows].sort(
    (left, right) => direction * compare(sortValue(left), sortValue(right)),
  );
};

export const sortableKeys = <TRow>(
  columns: readonly SortableColumn<TRow>[],
): string[] =>
  columns
    .filter((column) => column.sortValue !== undefined)
    .map((column) => column.key);
